// US-ROUTINE-01 -- Guardian creates a recurring task.

import {
  type AccountListEntry,
  type BucketEntry,
  type CreateRoutineInput,
  type RoutineEntry,
  routineCreate,
  routineUpdate,
} from '@chorepop/core/client';
import { type Component, For, Index, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MISS_BEHAVIOURS = [
  {
    value: 'expire_quietly',
    label: 'Expire quietly',
    desc: 'Closes silently; child is not notified.',
  },
  { value: 'record_miss', label: 'Record miss — no penalty', desc: 'Child sees a "Missed" entry.' },
  { value: 'penalty', label: 'Penalty only', desc: 'Deduct an amount from a bucket.' },
  {
    value: 'record_and_penalty',
    label: 'Record miss + penalty',
    desc: 'Missed entry AND a deduction.',
  },
] as const;

type Props = {
  sessionId: string;
  child: AccountListEntry;
  childBuckets: BucketEntry[];
  /** Active children available in the creation form's Assign to selector. */
  allChildren?: AccountListEntry[];
  routine?: RoutineEntry;
  onSaved: (recurringTaskId: number) => void;
  onCancel: () => void;
};

export const CreateRoutineScreen: Component<Props> = (props) => {
  const [taskName, setTaskName] = createSignal(props.routine?.taskName ?? '');
  const [taskNotes, setTaskNotes] = createSignal(props.routine?.taskNotes ?? '');
  const [reward, setReward] = createSignal(
    props.routine ? (props.routine.rewardMinorUnits / 100).toFixed(2) : '0',
  );
  const [scheduleDays, setScheduleDays] = createSignal<number[]>(props.routine?.scheduleDays ?? []);
  const [repeatIntervalWeeks, setRepeatIntervalWeeks] = createSignal(
    props.routine?.repeatIntervalWeeks ?? 1,
  );
  const [visibleTime, setVisibleTime] = createSignal(props.routine?.visibleTime ?? '07:00');
  const [deadlineTime, setDeadlineTime] = createSignal(props.routine?.deadlineTime ?? '21:00');
  const [missBehaviour, setMissBehaviour] = createSignal<string>(
    props.routine?.missBehaviour ?? 'expire_quietly',
  );
  const [penaltyBucketId, setPenaltyBucketId] = createSignal<number | null>(
    props.routine?.penaltyBucketId ?? null,
  );
  const [penaltyAmount, setPenaltyAmount] = createSignal(
    props.routine ? (props.routine.penaltyMinorUnits / 100).toFixed(2) : '0',
  );
  const [subTasks, setSubTasks] = createSignal<string[]>(
    props.routine?.subTasks.map((step) => step.subTaskName) ?? [],
  );
  const [newSubTask, setNewSubTask] = createSignal('');
  const [assignment, setAssignment] = createSignal<number | 'all'>(props.child.accountId);
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const availableChildren = () =>
    (props.allChildren?.length ?? 0) > 0 ? (props.allChildren ?? []) : [props.child];
  const creatingForAllChildren = () => !props.routine && assignment() === 'all';
  const assignedChild = () => {
    const selected = assignment();
    return typeof selected === 'number'
      ? availableChildren().find((child) => child.accountId === selected)
      : undefined;
  };
  const assignedCurrency = () =>
    assignedChild()?.displayCurrencyCode ?? props.child.displayCurrencyCode;
  const assignedBuckets = () => {
    const selected = assignedChild();
    if (!selected) return [];
    return props.childBuckets.filter((bucket) => bucket.childAccountId === selected.accountId);
  };

  const hasPenalty = () =>
    missBehaviour() === 'penalty' || missBehaviour() === 'record_and_penalty';

  const toggleDay = (d: number) =>
    setScheduleDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort(),
    );

  const addSubTask = () => {
    const label = newSubTask().trim();
    if (!label) return;
    setSubTasks((prev) => [...prev, label]);
    setNewSubTask('');
  };

  const removeSubTask = (i: number) => setSubTasks((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = async (e: Event) => {
    e.preventDefault();
    setError(null);

    if (!taskName().trim()) {
      setError('Task name is required.');
      return;
    }
    if (scheduleDays().length === 0) {
      setError('Choose at least one day.');
      return;
    }
    if (
      !Number.isInteger(repeatIntervalWeeks()) ||
      repeatIntervalWeeks() < 1 ||
      repeatIntervalWeeks() > 52
    ) {
      setError('Repeat interval must be a whole number from 1 to 52 weeks.');
      return;
    }
    if (visibleTime() >= deadlineTime()) {
      setError('The deadline must be later than the visible time.');
      return;
    }

    const rewardMinor = Math.round(Number.parseFloat(reward() || '0') * 100);
    const penaltyMinor = Math.round(Number.parseFloat(penaltyAmount() || '0') * 100);
    const selectedPenaltyBucketId = penaltyBucketId();
    if (creatingForAllChildren() && hasPenalty()) {
      setError('Set this routine up one child at a time when it has a bucket penalty.');
      return;
    }

    const selectedChild = assignedChild();
    if (!creatingForAllChildren() && !selectedChild) {
      setError('Choose a child for this routine.');
      return;
    }

    const input: CreateRoutineInput = {
      sessionId: props.sessionId,
      childAccountId: selectedChild?.accountId ?? props.child.accountId,
      taskName: taskName().trim(),
      taskNotes: taskNotes().trim(),
      rewardMinorUnits: rewardMinor,
      missBehaviour: missBehaviour(),
      ...(hasPenalty() && selectedPenaltyBucketId != null
        ? { penaltyBucketId: selectedPenaltyBucketId }
        : {}),
      penaltyMinorUnits: hasPenalty() ? penaltyMinor : 0,
      scheduleDays: scheduleDays(),
      repeatIntervalWeeks: repeatIntervalWeeks(),
      visibleTime: visibleTime(),
      deadlineTime: deadlineTime(),
      subTasks: subTasks(),
    };

    setSaving(true);
    if (props.routine) {
      const r = await routineUpdate({
        ...input,
        recurringTaskId: props.routine.recurringTaskId,
      });
      setSaving(false);
      if (!r.ok) {
        setError(r.error.message);
        return;
      }
      props.onSaved(props.routine.recurringTaskId);
      return;
    }

    const targets = creatingForAllChildren()
      ? availableChildren()
      : selectedChild
        ? [selectedChild]
        : [];
    let firstRoutineId: number | null = null;
    for (const child of targets) {
      const r = await routineCreate({ ...input, childAccountId: child.accountId });
      if (!r.ok) {
        setSaving(false);
        setError(
          creatingForAllChildren()
            ? `The routine was saved for some children, but not all: ${r.error.message}`
            : r.error.message,
        );
        return;
      }
      firstRoutineId ??= r.value.recurringTaskId;
    }
    setSaving(false);
    if (firstRoutineId !== null) props.onSaved(firstRoutineId);
  };

  return (
    <ActionScreen
      eyebrow={
        creatingForAllChildren()
          ? 'For every child'
          : `For ${assignedChild()?.displayName ?? props.child.displayName}`
      }
      title={props.routine ? 'Edit routine' : 'New routine'}
      onBack={props.onCancel}
      class="action-screen--routine-form"
      footer={
        <button
          type="submit"
          form="routine-form"
          class="primary action-screen-primary"
          disabled={saving()}
        >
          {saving() ? 'Saving…' : props.routine ? 'Save changes' : 'Save routine'}
        </button>
      }
    >
      <Show when={error()}>
        <div class="form-error" role="alert">
          {error()}
        </div>
      </Show>

      <Show when={creatingForAllChildren()}>
        <p class="action-screen-intro">
          This saves a separate routine for every active child. Each child gets their own checklist
          and progress.
        </p>
      </Show>

      <form id="routine-form" onSubmit={handleSave} class="routine-form">
        <Show when={!props.routine && (props.allChildren?.length ?? 0) > 0}>
          <div class="routine-form-field">
            <label class="action-field-label" for="rt-assignment">
              Assign to
            </label>
            <select
              id="rt-assignment"
              value={assignment()}
              disabled={saving()}
              aria-describedby={creatingForAllChildren() ? 'rt-assignment-hint' : undefined}
              onChange={(event) => {
                const selection = event.currentTarget.value;
                setAssignment(selection === 'all' ? 'all' : Number(selection));
                setPenaltyBucketId(null);
                setError(null);
              }}
              data-testid="create-routine-child-select"
            >
              <For each={availableChildren()}>
                {(child) => <option value={child.accountId}>{child.displayName}</option>}
              </For>
              <option value="all">All children — make one routine for each child</option>
            </select>
            <Show when={creatingForAllChildren()}>
              <span id="rt-assignment-hint" class="field-hint">
                This creates one assigned routine for every active child. It is not a shared
                checklist.
              </span>
            </Show>
          </div>
        </Show>

        {/* Name */}
        <div class="routine-form-field">
          <label for="rt-name" class="action-field-label">
            Task name *
          </label>
          <input
            id="rt-name"
            type="text"
            maxlength="80"
            value={taskName()}
            onInput={(e) => setTaskName(e.currentTarget.value)}
            placeholder="e.g. Bedtime routine"
            required
          />
        </div>

        {/* Notes for child */}
        <div class="routine-form-field">
          <label for="rt-notes" class="action-field-label">
            {creatingForAllChildren()
              ? 'Notes for each child (optional)'
              : `Notes for ${assignedChild()?.displayName ?? props.child.displayName} (optional)`}
          </label>
          <textarea
            id="rt-notes"
            rows={2}
            maxlength={300}
            value={taskNotes()}
            onInput={(e) => setTaskNotes(e.currentTarget.value)}
            placeholder="Any instructions or encouragement..."
          />
        </div>

        {/* Reward */}
        <div class="routine-form-card">
          <label for="rt-reward" class="action-field-label">
            Reward ({assignedCurrency()})
          </label>
          <div class="routine-amount-input">
            <span aria-hidden="true">£</span>
            <input
              id="rt-reward"
              type="number"
              min="0"
              step="0.01"
              inputmode="decimal"
              value={reward()}
              onInput={(e) => setReward(e.currentTarget.value)}
            />
          </div>
          <span class="field-hint">Set to 0 for a no-reward routine.</span>
        </div>

        {/* Schedule days */}
        <fieldset class="routine-form-card routine-form-fieldset">
          <legend class="action-field-label">Repeat on *</legend>
          <div class="day-picker">
            <For each={DAY_LABELS}>
              {(label, i) => (
                <button
                  type="button"
                  class={scheduleDays().includes(i()) ? 'day-btn day-btn--on' : 'day-btn'}
                  onClick={() => toggleDay(i())}
                  aria-pressed={scheduleDays().includes(i())}
                >
                  {label}
                </button>
              )}
            </For>
          </div>
        </fieldset>

        <div class="routine-form-card routine-repeat-interval">
          <label for="rt-repeat-interval" class="action-field-label">
            Repeat every
          </label>
          <div class="routine-repeat-interval-control">
            <input
              id="rt-repeat-interval"
              type="number"
              min="1"
              max="52"
              step="1"
              inputmode="numeric"
              value={repeatIntervalWeeks()}
              onInput={(event) => setRepeatIntervalWeeks(Number(event.currentTarget.value))}
              data-testid="routine-repeat-interval-weeks"
            />
            <span>{repeatIntervalWeeks() === 1 ? 'week' : 'weeks'}</span>
          </div>
          <span class="field-hint">
            For example, choose 3 to show this routine on the selected days every three weeks.
          </span>
        </div>

        {/* Times */}
        <div class="routine-time-grid">
          <div class="routine-form-field">
            <label for="rt-visible" class="action-field-label">
              Visible from
            </label>
            <input
              id="rt-visible"
              type="time"
              value={visibleTime()}
              onInput={(e) => setVisibleTime(e.currentTarget.value)}
            />
          </div>
          <div class="routine-form-field">
            <label for="rt-deadline" class="action-field-label">
              Deadline
            </label>
            <input
              id="rt-deadline"
              type="time"
              value={deadlineTime()}
              onInput={(e) => setDeadlineTime(e.currentTarget.value)}
            />
          </div>
        </div>

        {/* Sub-tasks */}
        <div class="routine-form-card">
          <label for="rt-new-subtask" class="action-field-label">
            Steps (optional checklist)
          </label>
          <Show when={subTasks().length > 0}>
            <ol class="subtask-list">
              <Index each={subTasks()}>
                {(item, i) => (
                  <li class="subtask-item">
                    <span>{item()}</span>
                    <button
                      type="button"
                      class="ghost subtask-remove"
                      onClick={() => removeSubTask(i)}
                    >
                      Remove
                    </button>
                  </li>
                )}
              </Index>
            </ol>
          </Show>
          <div class="routine-subtask-add-row">
            <input
              id="rt-new-subtask"
              type="text"
              placeholder="Add a step..."
              maxlength="80"
              value={newSubTask()}
              onInput={(e) => setNewSubTask(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addSubTask();
                }
              }}
            />
            <button type="button" class="ghost" onClick={addSubTask}>
              Add
            </button>
          </div>
          <span class="field-hint">Leave empty for a single-tap completion.</span>
        </div>

        {/* Miss behaviour */}
        <fieldset class="routine-form-card routine-form-fieldset">
          <legend class="action-field-label">If missed</legend>
          <For each={MISS_BEHAVIOURS}>
            {(opt) => (
              <label class="routine-miss-option">
                <input
                  type="radio"
                  name="miss"
                  value={opt.value}
                  checked={missBehaviour() === opt.value}
                  onChange={() => setMissBehaviour(opt.value)}
                />
                <span class="routine-miss-copy">
                  <strong>{opt.label}</strong>
                  <span class="field-hint">{opt.desc}</span>
                </span>
              </label>
            )}
          </For>
        </fieldset>

        {/* Penalty config */}
        <Show when={hasPenalty()}>
          <div class="routine-form-card">
            <label for="rt-penalty-bucket" class="action-field-label">
              Deduct from bucket
            </label>
            <select
              id="rt-penalty-bucket"
              value={penaltyBucketId() ?? ''}
              onChange={(e) => setPenaltyBucketId(Number(e.currentTarget.value) || null)}
            >
              <option value="">-- choose bucket --</option>
              <For each={assignedBuckets()}>
                {(b) => <option value={b.bucketId}>{b.bucketName}</option>}
              </For>
            </select>
          </div>
          <div class="routine-form-card">
            <label for="rt-penalty-amt" class="action-field-label">
              Penalty amount ({assignedCurrency()})
            </label>
            <div class="routine-amount-input">
              <span aria-hidden="true">£</span>
              <input
                id="rt-penalty-amt"
                type="number"
                min="0.01"
                step="0.01"
                inputmode="decimal"
                value={penaltyAmount()}
                onInput={(e) => setPenaltyAmount(e.currentTarget.value)}
              />
            </div>
          </div>
        </Show>
      </form>
    </ActionScreen>
  );
};
